import { Dictionary, flatten, get, keys, pickBy } from 'lodash';
import { getArguments } from './templates';
import {
  FlattenedMixinSyntaxTree,
  friendlyMixinOperation,
  isAttributeMixin,
  isLegacyOperation,
  isMixinOperandType,
  isMixinOperationType,
  leftHandedMixins,
  MixinOperandType,
  MixinOperationType,
  MixinSyntaxTree,
  rightHandedMixins,
  valueChainMixins,
} from './types';

import _parser from './parser';
const parser: MixinParser = _parser;

export function parseMixin(expression: string) {
  const parsed = parser.parse(expression);
  const cleaned = cleanSyntaxTree(parsed);
  if (!isPureMixinTree(cleaned)) {
    throw Error(`Found mix of legacy and v2 operators in: ${expression}`);
  }
  return cleaned;
}

function cleanSyntaxTree(syntaxTree: MixinSyntaxTree, root = true): MixinSyntaxTree {
  // Breaking changes may require database mixin migration
  if (isMixinOperandType(syntaxTree.type)) {
    return syntaxTree;
  } else if (
    syntaxTree.type === MixinOperationType.MIXIN &&
    (root || [MixinOperationType.MIXIN, MixinOperandType.PRODUCT].includes(syntaxTree.arguments[0].type))
  ) {
    return cleanSyntaxTree(syntaxTree.arguments[0], root);
  } else {
    return {
      type: syntaxTree.type as MixinOperationType,
      arguments: (syntaxTree.arguments as MixinSyntaxTree[]).map(arg => cleanSyntaxTree(arg, false)),
    };
  }
}

export function toExpression(tree: MixinSyntaxTree, root = true): string {
  // Breaking changes may require database mixin migration
  if (isMixinOperandType(tree.type)) {
    return tree.arguments[0] as string;
  } else if (tree.type === MixinOperationType.MIXIN) {
    return toExpression(tree.arguments[0], false);
  } else if (tree.type === MixinOperationType.AND) {
    const parts = (tree.arguments as MixinSyntaxTree[]).map(arg => toExpression(arg, false));
    const expression = parts.join(',');
    return root ? expression : `(${expression})`;
  } else if (leftHandedMixins.includes(tree.type as MixinOperationType)) {
    const expression = `${toExpression(tree.arguments[0] as MixinSyntaxTree, false)} ${tree.type}`;
    return root ? expression : `(${expression})`;
  } else if (rightHandedMixins.includes(tree.type as MixinOperationType)) {
    const expression = `${tree.type} ${toExpression(tree.arguments[0] as MixinSyntaxTree, false)}`;
    return root ? expression : `(${expression})`;
  } else {
    const firstPart = toExpression(tree.arguments[0] as MixinSyntaxTree, false);
    const maybeSecond = (tree.arguments.slice(1) as [MixinSyntaxTree]).map(arg => toExpression(arg, false)).join(' ');
    const expression = `${firstPart} ${tree.type} ${maybeSecond}`;
    return root ? expression : `(${expression})`;
  }
}

export function isValidExpression(expression: string) {
  try {
    parseMixin(expression);
    return true;
  } catch (e) {
    return false;
  }
}

export function mixinErrorInfo(error: Error, expression: string) {
  const message = error.message;
  // eslint-disable-next-line no-console
  console.log(message);
  const lines = message.split('\n');
  const expressionExtract = lines[1];
  if (!expressionExtract) {
    return message;
  }
  const info = lines[3] || 'Unrecognized';
  const prelude = expressionExtract.replace('...', '');
  const errorIdx = expression.indexOf(prelude) + prelude.length;
  const marker = ' '.repeat(errorIdx - 1) + '^';
  return `${marker} ${info}`;
}

interface MixinParser {
  parse: (str: string) => MixinSyntaxTree;
}

export function extractProductCodes(expression: string) {
  const tree = parseMixin(expression);
  return getProductCodes(tree);
}

export function getProductCodes(tree: MixinSyntaxTree): string[] {
  switch (tree.type) {
    case MixinOperandType.PRODUCT:
      return [tree.arguments[0]];
    case MixinOperandType.ATTRIBUTE:
    case MixinOperandType.NUMBER:
    case MixinOperandType.TEMPLATE_ARGUMENT:
      return [];
    default:
      return flatten(tree.arguments.map(operand => getProductCodes(operand as MixinSyntaxTree)));
  }
}

export function* operationsGenerator(tree: MixinSyntaxTree): Generator<MixinOperationType> {
  if (isMixinOperationType(tree.type)) {
    yield tree.type as MixinOperationType;
    for (const argument of tree.arguments) {
      yield* operationsGenerator(argument as unknown as MixinSyntaxTree);
    }
  }
}

function isPureMixinTree(tree: MixinSyntaxTree): boolean {
  const operators = Array.from(operationsGenerator(tree)).filter(
    e =>
      ![
        MixinOperationType.MIXIN,
        MixinOperationType.ALIAS,
        MixinOperationType.SCALE_BY,
        MixinOperationType.ADD_BY,
      ].includes(e),
  );
  return operators.every(isLegacyOperation) || !operators.some(isLegacyOperation);
}

export function getCanonicalForm(expression: string) {
  const syntaxTree = parseMixin(expression);
  return toExpression(syntaxTree);
}

function isComputableSyntaxTree(tree: MixinSyntaxTree): boolean {
  return getArguments(tree).length === 0;
}

export function assertComputableSyntaxTree(tree: MixinSyntaxTree) {
  if (!isComputableSyntaxTree(tree)) {
    throw new Error(`Could not compute mixin with expression: ${toExpression(tree)}`);
  }
}

export function mixinTitle(mixin: MixinSyntaxTree, products: Dictionary<{ title: string }>): string {
  const res = mixinTitleRecursive(mixin, products);
  return res[0].toUpperCase() + res.slice(1);
}

function mixinTitleRecursive(mixin: MixinSyntaxTree, products: Dictionary<{ title: string }>): string {
  if (mixin.type === MixinOperandType.PRODUCT) {
    const product = get(products, mixin.arguments[0], undefined);
    if (!product) {
      throw Error(`Unknown product ${mixin.arguments[0]}`);
    }
    return products[mixin.arguments[0]].title.toLowerCase();
  } else if (isMixinOperandType(mixin.type)) {
    return mixin.arguments[0] as string;
  } else if (mixin.type === MixinOperationType.MIXIN) {
    return mixinTitleRecursive(mixin.arguments[0], products);
  } else if (mixin.type === MixinOperationType.AND) {
    const titles = (mixin.arguments as MixinSyntaxTree[]).map(arg => mixinTitleRecursive(arg, products));
    return titles.join(' and ');
  } else {
    const firstTitle = mixinTitleRecursive(mixin.arguments[0] as MixinSyntaxTree, products);
    const maybeSecondTitle = (mixin.arguments as [MixinSyntaxTree])
      .slice(1)
      .map(arg => mixinTitleRecursive(arg, products));
    const operation = friendlyMixinOperation(mixin.type as MixinOperationType);

    return renderMixinTitle(operation, firstTitle, ...maybeSecondTitle);
  }
}

function renderMixinTitle(str: string, ...titles: string[]) {
  return titles.reduce((a, v, index) => a.replace(`{${index}}`, v), str);
}

export function flattenSyntaxTree(
  syntaxTree: MixinSyntaxTree | undefined,
  parent?: string | undefined,
): FlattenedMixinSyntaxTree {
  if (!syntaxTree || isMixinOperandType(syntaxTree.type)) {
    return {};
  }
  const leftBranch = syntaxTree.arguments[0];
  const leftExpression = toExpression(leftBranch as MixinSyntaxTree);

  const rightBranch = mixinRightSide(syntaxTree);
  const rightExpression = rightBranch ? toExpression(rightBranch as MixinSyntaxTree) : undefined;

  if (syntaxTree.type === MixinOperationType.MIXIN) {
    return flattenSyntaxTree(leftBranch as MixinSyntaxTree, parent);
  }

  // Recursion step for nested mixins
  const mixinId = toExpression(syntaxTree);
  const branches = {
    ...flattenSyntaxTree(rightBranch as MixinSyntaxTree, mixinId),
    ...flattenSyntaxTree(leftBranch as MixinSyntaxTree, mixinId),
  };

  const ids = Object.keys(branches);
  // Make sure we keep only one level of hierarchy
  const existingChildren = ids.reduce<string[]>((a, v) => [...a, ...(branches[v].children || [])], []);
  const children = ids.filter(k => get(branches, `${k}.parent`, undefined) && !existingChildren.includes(k));

  const operator = syntaxTree.type;
  return {
    [mixinId]: { leftExpression, rightExpression, mixinId, parent, children, operator },
    ...branches,
  };
}

function mixinRightSide(syntaxTree: MixinSyntaxTree) {
  if (isAttributeMixin(syntaxTree.type)) {
    return undefined;
  }
  return syntaxTree.arguments.at(1);
}

export function maybeParseMixin(expression: string): MixinSyntaxTree | null {
  try {
    return parseMixin(expression);
  } catch (_) {
    return null;
  }
}

export function unfoldMixinExpression(
  expression: string,
  expressionsByCode: Dictionary<string>,
  previouslyVisited: Set<string> = new Set<string>(),
): string {
  const productCodes = extractProductCodes(expression);
  const chainedExpByCode = pickBy(expressionsByCode, (_, code) => productCodes.includes(code));
  const chainedCodes = keys(chainedExpByCode);

  if (chainedCodes.some(code => previouslyVisited.has(code))) {
    throw new Error(`Loop detected while unfolding mixin expression "${expression}"`);
  }
  const marked = new Set([...previouslyVisited.values(), ...chainedCodes]);

  return chainedCodes.reduce<string>((acc, code) => {
    const exp = chainedExpByCode[code];
    const unfoldedExp = unfoldMixinExpression(exp, expressionsByCode, marked);
    return acc.replace(code, `(${unfoldedExp})`);
  }, expression);
}

export function hasValueChainMixin(expression: string) {
  const tree = parseMixin(expression);
  const operators = Array.from(operationsGenerator(tree));
  return operators.some(o => valueChainMixins.includes(o));
}

export function isValueChainMixin(expression: string) {
  const tree = parseMixin(expression);
  const operators = Array.from(operationsGenerator(tree));
  return operators.every(o => valueChainMixins.includes(o));
}
